import { useState } from 'react'
import { cn } from '@/lib/utils'

/** A drag under way: the title cross-fades toward `to` by `q` (0–1), sliding the way the calendar travels. */
export interface TitleFollow {
  to: string
  q: number
  dir: 1 | -1
  /** Released: ease the rest of the way instead of tracking the finger. */
  easing: boolean
}

/**
 * The plans calendar's month title. Mid-drag it follows the finger; otherwise a
 * change cross-fades on its own, unless `quiet` (a drag already showed it).
 * Each title is only as wide as its text: a full-width animating box under the
 * month arrows would re-raster them mid-animation.
 */
export function PlansCalendarTitle({ month, format, follow, quiet }: {
  month: string
  format: (month: string) => string
  follow: TitleFollow | null
  quiet: boolean
}) {
  const [prev, setPrev] = useState(month)
  const [dir, setDir] = useState<-1 | 0 | 1>(0)
  /** The title being replaced, fading out under the new one. */
  const [leaving, setLeaving] = useState<string | null>(null)
  if (prev !== month) {
    setPrev(month)
    setLeaving(quiet ? null : prev)
    setDir(quiet ? 0 : month > prev ? 1 : -1)
  }

  if (follow) {
    const ease = follow.easing && 'transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none'
    return (
      <span className="relative flex-1 overflow-hidden text-sm font-semibold capitalize">
        <span className={cn('block w-fit whitespace-nowrap', ease)}
          style={{ opacity: 1 - follow.q, transform: `translateX(${-follow.dir * follow.q}rem)` }}>
          {format(month)}
        </span>
        <span aria-hidden className={cn('absolute left-0 top-0 whitespace-nowrap', ease)}
          style={{ opacity: follow.q, transform: `translateX(${follow.dir * (1 - follow.q)}rem)` }}>
          {format(follow.to)}
        </span>
      </span>
    )
  }

  return (
    <span className="relative flex-1 overflow-hidden text-sm font-semibold capitalize">
      {leaving && (
        <span key={leaving} aria-hidden onAnimationEnd={() => setLeaving(null)}
          className={cn('absolute left-0 top-0 whitespace-nowrap animate-out fade-out fill-mode-forwards duration-300 ease-in-out motion-reduce:hidden',
            dir > 0 ? 'slide-out-to-left-4' : 'slide-out-to-right-4')}>
          {format(leaving)}
        </span>
      )}
      <span key={month} className={cn('block w-fit whitespace-nowrap',
        dir !== 0 && 'animate-in fade-in duration-300 ease-in-out motion-reduce:animate-none',
        dir > 0 ? 'slide-in-from-right-4' : 'slide-in-from-left-4')}>
        {format(month)}
      </span>
    </span>
  )
}
